import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Spinner,
  Table,
} from "reactstrap";

// react table
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchServicesByCompany } from "../../../slices/apps/orderSlice";
import { fetchServiceCommissionSummary } from "../../../slices/apps/companySlice";

// sayfalar, modallar vb.
import DebouncedInput from "../../../Components/Common/DebouncedInput";
import TablePagination from "../../../Components/Common/TablePagination";
import RequestPaymentModal from "./RequestPaymentModal";

// Column Definitions
const columnHelper = createColumnHelper();

const pageSizeOptions = [10, 25, 50, 100, 200];

const currencyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
});

const IndeterminateCheckbox = ({ indeterminate, ...rest }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof indeterminate === "boolean" && ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <input type="checkbox" ref={ref} {...rest} style={{ cursor: "pointer" }} />
  );
};

const ServiceTable = ({ id }) => {
  const dispatch = useDispatch();

  const { servicesByCompany, servicesByCompanyLoading } = useSelector(
    (state) => {
      return {
        servicesByCompany: state.order.servicesByCompany,
        servicesByCompanyLoading: state.order.servicesByCompanyLoading,
      };
    },
    shallowEqual
  );

  // States
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [filterStatus, setFilterStatus] = useState("");
  const [rowSelection, setRowSelection] = useState({});

  useEffect(() => {
    if (!servicesByCompany) {
      setData([]);
      return;
    }
    const filteredData = servicesByCompany?.filter((data) => {
      if (filterStatus && data.isActive.toString() !== filterStatus)
        return false;
      if (globalFilter) {
        const searchTerm = globalFilter.toLocaleLowerCase("tr");
        const searchableFields = [
          data.orderNo,
          data.status,
          data.commissionFee,
        ];
        const match = searchableFields.some(
          (field) =>
            field &&
            field.toString().toLocaleLowerCase("tr").includes(searchTerm)
        );
        if (!match) return false;
      }
      return true;
    });
    setData(filteredData);
  }, [globalFilter, filterStatus, servicesByCompany]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: ({ table }) => (
          <div>
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllPageRowsSelected(),
                indeterminate: table.getIsSomePageRowsSelected(),
                onChange: table.getToggleAllPageRowsSelectedHandler(),
              }}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div>
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
        size: 46,
        minSize: 46,
        maxSize: 46,
        enableSorting: false,
      }),
      columnHelper.accessor("orderNo", {
        header: "Servis No",
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.orderNo}
          </div>
        ),
      }),
      columnHelper.accessor("customerName", {
        header: "Müşteri Adı Soyadı",
        size: "auto",
        minSize: 150,
        maxSize: 250,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.customerName}
          </div>
        ),
      }),
      columnHelper.accessor("status", {
        header: "Durum",
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.status}
          </div>
        ),
      }),
      columnHelper.accessor("commissionFee", {
        header: "Komisyon Tutarı",
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {currencyFormatter.format(row.original.commissionFee)}
          </div>
        ),
      }),

      columnHelper.accessor("creationDate", {
        header: "Servis Tarihi",
        size: 160,
        minSize: 160,
        maxSize: 160,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {new Date(row.original.creationDate).toLocaleString("tr-TR")}
          </div>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: data,
    columns,
    state: {
      pagination,
      rowSelection,
    },
    enableRowSelection: (row) => row.original.status === "Hak Ediş İstenmedi",
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const selectedRowIds = useMemo(() => {
    return table.getSelectedRowModel().flatRows.map((row) => row.original.id);
  }, [rowSelection]);

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  const refreshData = async () => {
    try {
      await dispatch(fetchServicesByCompany(id)).unwrap();
      await dispatch(fetchServiceCommissionSummary(id)).unwrap();
    } catch (error) { /* Silently handled */ }
  };

  return (
    <Fragment>
      <Card>
        <CardBody className="d-flex align-items-center gap-2">
          <Button
            className="btn btn-label btn"
            color="warning"
            type="button"
            onClick={() => refreshData()}
          >
            <i className="ri-refresh-line label-icon align-middle me-2"></i>
            Yenile
          </Button>
          <RequestPaymentModal ids={selectedRowIds || []} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1 fs-14">Servis Listesi</h4>
          <div className="flex-shrink-0"></div>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <CardBody className="border border-dashed border-end-0 border-start-0 border-top-0">
              <form>
                <div className="d-flex flex-wrap justify-content-between">
                  <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                    <div style={{ width: "100px" }}>
                      <select
                        className="form-select mb-3"
                        aria-label="Sayfa başına kayıt sayısı"
                        value={getState().pagination.pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        {pageSizeOptions.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div
                      className="search-box me-2 mb-2 d-inline-block border"
                      style={{ borderRadius: "0.25em" }}
                    >
                      <DebouncedInput
                        value={globalFilter ?? ""}
                        onChange={(value) => setGlobalFilter(value)}
                        placeholder={"Ara..."}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
              </form>
              <Row>
                <Col sm={6} md={4} lg={3} xl={2}>
                  <label className="form-label">Durum</label>
                  <select
                    className="form-select mb-3"
                    aria-label="Durum Filtresi"
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                    }}
                  >
                    <option value="">Tümü</option>
                    <option value={true}>Aktif</option>
                    <option value={false}>Pasif</option>
                  </select>
                </Col>
              </Row>
            </CardBody>
          </Row>

          <div
            style={{
              overflowX: "auto",
            }}
          >
            <Table className="align-middle">
              <thead>
                {getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="text-muted"
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize,
                          letterSpacing: "0.8px",
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: header.column.getCanSort()
                                ? "pointer"
                                : "default",
                              userSelect: "none",
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <i className="ri-arrow-down-s-line"></i>,
                              desc: <i className="ri-arrow-up-s-line"></i>,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {servicesByCompanyLoading ? (
                  <tr>
                    <td colSpan="100%" className="text-center pt-5 pb-5">
                      <Spinner color="primary" />
                      <p className="text-dark">Yükleniyor...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="100%"
                      className="text-center pt-5 pb-5 text-muted"
                    >
                      <h1>
                        <i className="ri-error-warning-line text-muted"></i>
                      </h1>
                      <p>Kayıt bulunamadı!</p>
                    </td>
                  </tr>
                ) : (
                  getRowModel().rows.map((row) => {
                    return (
                      <tr
                        key={row.id}
                        className={row.getIsSelected() ? "table-active" : ""}
                      >
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              style={{
                                width: cell.column.columnDef.size,
                                minWidth: cell.column.columnDef.minSize,
                                maxWidth: cell.column.columnDef.maxSize,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <TablePagination table={table} />
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default ServiceTable;
